import { createStart } from '@tanstack/react-start';
import { setVibeWsTokenFetcher } from '@vibe/db/client';
import { ensureVibeGuest } from '@api/ensure-vibe-guest';
import { getVibeWsToken } from '@api/vibe-ws-token';
import { getAuthToken, installVibeAuthBridge, VIBE_SERVER_URL } from '@lib/vibe-auth';
import { getStoredGuestToken, setStoredGuestToken } from '@lib/vibe-guest';

// Shared client+server Start configuration.
//
// Cross-origin server functions: client (CDN) and server (monday-code Lambda) live on different
// origins. Start's client RPC stub fetches a RELATIVE url (/_serverFn/<id>) by default, which
// would resolve against the CDN origin. We override the global server-fn fetch so those calls
// target the Lambda's absolute origin (__VIBE_SERVER_URL__) and carry the monday bearer token
// from the postMessage bridge.
//
// CSRF: intentionally opted out (empty requestMiddleware so Start doesn't auto-install its CSRF
// guard). Our security boundary is CORS (index.js, trusted-origin allowlist) + the bearer token —
// we don't use cookies, so cookie-CSRF doesn't apply. The auto CSRF guard also rejects the
// build-time SPA-shell prerender request (no Origin header), so opting out is required anyway.

const isBrowser = typeof window !== 'undefined';

if (isBrowser) {
  installVibeAuthBridge();
  // The realtime socket mints its WS token through the app's own Lambda (this server fn), never the
  // builder backend — same origin + auth as every other server-fn call.
  setVibeWsTokenFetcher(() => getVibeWsToken());
  // Public/external apps: establish a DO-issued guest identity before realtime/writes need it, and
  // persist the returned opaque token. Members (private/internal) get { guestToken: null } and skip.
  // ponytail: fire-and-persist; a write/WS-mint in the brief window before this resolves is simply
  // unidentified (actor falls back to 'service' / socket retries) — no incorrect attribution.
  ensureVibeGuest()
    .then((r) => {
      if (r.guestToken) setStoredGuestToken(r.guestToken);
    })
    .catch(() => {
      // transient — the next server-fn round-trip re-establishes it
    });
}

function toAbsoluteServerUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === 'string' && input.startsWith('/')) {
    return `${VIBE_SERVER_URL}${input}`;
  }
  if (input instanceof URL && input.origin === window.location.origin) {
    return `${VIBE_SERVER_URL}${input.pathname}${input.search}`;
  }
  return input;
}

export const startInstance = createStart(() => ({
  requestMiddleware: [],
  serverFns: {
    fetch: isBrowser
      ? (input: RequestInfo | URL, init?: RequestInit) => {
          const token = getAuthToken();
          const headers = new Headers(init?.headers);
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          // Public/external apps have no monday bearer: attach the browser's opaque DO-issued guest
          // token so the server resolves the anonymous viewer's DO-owned id. Harmless in private
          // mode (server ignores it). Empty on the very first load until ensureVibeGuest() resolves.
          const guestToken = getStoredGuestToken();
          if (guestToken) {
            headers.set('x-vibe-guest-token', guestToken);
          }
          return fetch(toAbsoluteServerUrl(input), { ...init, headers });
        }
      : undefined,
  },
}));
